import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Avatar,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  Delete as DeleteIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import type { SquareRequest } from '../../types';
import { squareRequestsApi } from '../../api';

export interface SquareRequestsPanelProps {
  boardId: string;
  isManager: boolean;
  currentUserId?: string;
  onRequestStatusChange?: () => void; // Called when a request is approved/denied
  refreshTrigger?: number; // Increment to trigger a refresh from parent (SSE events)
}

type RequestFilter = 'all' | 'pending' | 'approved' | 'denied';

export const SquareRequestsPanel: React.FC<SquareRequestsPanelProps> = ({ boardId, isManager, currentUserId, onRequestStatusChange, refreshTrigger }) => {
  const [requests, setRequests] = useState<SquareRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RequestFilter>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [approveAllConfirmOpen, setApproveAllConfirmOpen] = useState(false);
  const [approveAllLoading, setApproveAllLoading] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch all requests, we'll filter client-side for better UX
      const data = await squareRequestsApi.list(boardId, '');
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Refresh when SSE triggers a refresh
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchRequests();
    }
  }, [refreshTrigger, fetchRequests]);

  // Filter requests client-side
  const filteredRequests = useMemo(() => {
    if (filter === 'all') return requests;
    return requests.filter(r => r.status === filter);
  }, [requests, filter]);

  // Count by status for filter badges
  const counts = useMemo(() => ({
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    denied: requests.filter(r => r.status === 'denied').length,
  }), [requests]);

  const handleApprove = async (requestId: string, approvedSquares?: number) => {
    setActionLoading(requestId);
    try {
      await squareRequestsApi.approve(boardId, requestId, { approvedSquares });
      fetchRequests();
      onRequestStatusChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeny = async (requestId: string, reason?: string) => {
    setActionLoading(requestId);
    try {
      await squareRequestsApi.deny(boardId, requestId, { reason });
      fetchRequests();
      onRequestStatusChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deny request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = (requestId: string) => {
    setDeleteConfirmId(requestId);
  };

  const doDeleteRequest = async () => {
    if (!deleteConfirmId) return;
    setActionLoading(deleteConfirmId);
    try {
      await squareRequestsApi.delete(boardId, deleteConfirmId);
      fetchRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete request');
    } finally {
      setActionLoading(null);
      setDeleteConfirmId(null);
    }
  };

  const handleApproveAll = async () => {
    const pendingRequests = requests.filter(r => r.status === 'pending');
    if (pendingRequests.length === 0) return;

    setApproveAllLoading(true);
    try {
      await squareRequestsApi.approveAll(boardId);
      fetchRequests();
      onRequestStatusChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve all requests');
    } finally {
      setApproveAllLoading(false);
      setApproveAllConfirmOpen(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'denied': return 'error';
      default: return 'warning';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <ApproveIcon fontSize="small" />;
      case 'denied': return <DenyIcon fontSize="small" />;
      default: return <PendingIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Explanation box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {isManager
            ? 'Review and manage square requests from players. Approve or deny requests to control who can claim squares.'
            : 'View the status of square requests. Approved requests will show how many squares you can claim.'
          }
          {' '}
          <Link component={RouterLink} to="/support/boards#square-requests">
            Learn more →
          </Link>
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 2 }}>
        <Typography variant="h6">Square Requests ({requests.length})</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`All (${counts.all})`}
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'filled' : 'outlined'}
            sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } }}
          />
          <Chip
            label={`Pending (${counts.pending})`}
            onClick={() => setFilter('pending')}
            color={filter === 'pending' ? 'warning' : 'default'}
            variant={filter === 'pending' ? 'filled' : 'outlined'}
            icon={<PendingIcon />}
            sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } }}
          />
          <Chip
            label={`Approved (${counts.approved})`}
            onClick={() => setFilter('approved')}
            color={filter === 'approved' ? 'success' : 'default'}
            variant={filter === 'approved' ? 'filled' : 'outlined'}
            icon={<ApproveIcon />}
            sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } }}
          />
          <Chip
            label={`Denied (${counts.denied})`}
            onClick={() => setFilter('denied')}
            color={filter === 'denied' ? 'error' : 'default'}
            variant={filter === 'denied' ? 'filled' : 'outlined'}
            icon={<DenyIcon />}
            sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } }}
          />
        </Box>
      </Box>

      {/* Approve All button - only for managers when there are pending requests */}
      {isManager && counts.pending > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            onClick={() => setApproveAllConfirmOpen(true)}
            disabled={approveAllLoading}
          >
            {approveAllLoading ? 'Approving...' : `Approve All (${counts.pending})`}
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {filteredRequests.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {filter !== 'all' ? `No ${filter} requests` : 'No requests yet'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredRequests.map((request) => (
            <Paper key={request.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Request info */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'primary.main', borderRadius: 1 }}>
                      {request.user?.displayName?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {request.user?.displayName || 'Unknown User'}
                    </Typography>
                    <Chip
                      size="small"
                      icon={getStatusIcon(request.status)}
                      label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      color={getStatusColor(request.status) as 'success' | 'error' | 'warning'}
                    />
                  </Box>
                  <Typography variant="body2">
                    Requested: <strong>{request.requestedSquares} squares</strong>
                    {request.approvedSquares !== undefined && request.approvedSquares !== null && (
                      <> | Approved: <strong>{request.approvedSquares} squares</strong></>
                    )}
                  </Typography>
                  {request.denialReason && (
                    <Typography variant="body2" color="error">
                      Reason: {request.denialReason}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Submitted: {new Date(request.createdAt).toLocaleString()}
                    {request.reviewedAt && (
                      <> | Reviewed: {new Date(request.reviewedAt).toLocaleString()} by {request.reviewedBy?.displayName || 'Unknown'}</>
                    )}
                  </Typography>
                </Box>
                {/* Action buttons - separate row */}
                {(request.status === 'pending' && (isManager || request.userId === currentUserId)) && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {request.status === 'pending' && isManager && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            color="success"
                            onClick={() => handleApprove(request.id)}
                            disabled={actionLoading === request.id}
                            sx={{ border: 1, borderColor: 'success.main' }}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deny">
                          <IconButton
                            color="error"
                            onClick={() => handleDeny(request.id)}
                            disabled={actionLoading === request.id}
                            sx={{ border: 1, borderColor: 'error.main' }}
                          >
                            <DenyIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(isManager || request.userId === currentUserId) && request.status === 'pending' && (
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => handleDelete(request.id)}
                          disabled={actionLoading === request.id}
                          sx={{ border: 1, borderColor: 'grey.400' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Delete Request Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteConfirmId}
        title="Delete Request"
        icon="warning"
        message="Are you sure you want to delete this request?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={doDeleteRequest}
        onCancel={() => setDeleteConfirmId(null)}
      />

      {/* Approve All Confirmation Dialog */}
      <ConfirmDialog
        open={approveAllConfirmOpen}
        title="Approve All Pending Requests"
        icon="warning"
        confirmLabel="Approve All"
        confirmColor="success"
        isLoading={approveAllLoading}
        onConfirm={handleApproveAll}
        onCancel={() => setApproveAllConfirmOpen(false)}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to approve all <strong>{counts.pending}</strong> pending requests?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Each player will be approved for the number of squares they requested. You can adjust individual approvals afterward if needed.
        </Typography>
      </ConfirmDialog>
    </Box>
  );
};

export default SquareRequestsPanel;
